import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Leaf, FileText, BarChart3, Users, Building, Shield, ArrowRight, CheckCircle2 } from 'lucide-react';
import SEO, { generateServiceSchema } from '@/components/SEO';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const SustainabilityPage = () => {
  const { locale } = useParams();

  const pillars = [
    {
      title: 'BRSR Report Generation',
      description: 'Audit-ready, SEBI-compliant BRSR reports with full data capture, stakeholder mapping, and disclosure documentation',
      icon: FileText
    },
    {
      title: 'ESG Strategy & Implementation',
      description: 'Embedding sustainability into operations, supply chains, and governance — beyond just reporting',
      icon: BarChart3
    }
  ];

  const coverage = {
    environment: ['Emissions Management', 'Energy Efficiency', 'Water & Waste'],
    social: ['Employee Wellbeing', 'Community Impact', 'Supply Chain Responsibility'],
    governance: ['Board Diversity', 'Ethics & Transparency', 'Risk Management']
  };

  const process = [
    { step: '01', title: 'ESG Materiality Assessment', description: 'Identify material ESG topics relevant to your business' },
    { step: '02', title: 'Data Collection & Gap Analysis', description: 'Comprehensive data gathering and compliance gap identification' },
    { step: '03', title: 'BRSR Report Drafting & Review', description: 'Expert drafting with multiple review cycles' },
    { step: '04', title: 'Submission Support', description: 'Filing support and stakeholder communication guidance' }
  ];

  const frameworks = ['GRI', 'SASB', 'TCFD', 'CDP', 'UN SDGs', 'SEBI BRSR'];

  const serviceSchema = generateServiceSchema({
    type: 'ESG Consulting',
    name: 'Sustainability & ESG / BRSR Consulting',
    description: 'SEBI-mandated BRSR report generation and ESG strategy implementation for listed companies.'
  });

  return (
    <div className="pt-20" data-testid="sustainability-page">
      <SEO 
        title="Sustainability & ESG Consulting"
        description="SEBI BRSR compliance and ESG strategy for India's top listed companies. Transform regulatory requirements into competitive advantage with expert sustainability advisory."
        path="/services/sustainability"
        structuredData={serviceSchema}
      />
      {/* Hero */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-white via-light-grey to-white overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-teal/5" />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
              <span className="inline-flex items-center px-4 py-2 bg-teal/10 text-teal rounded-full text-sm font-medium mb-6">
                <Leaf className="w-4 h-4 mr-2" />
                ESG Advisory
              </span>
              <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-navy leading-tight mb-6">
                Sustainability Consulting & BRSR Reporting
              </h1>
              <p className="text-lg text-mid-grey leading-relaxed mb-8">
                Transform regulatory compliance into competitive advantage with expert ESG guidance.
              </p>
              <Link to={`/${locale}/contact`} className="btn-primary">
                Get Your BRSR Assessment
              </Link>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="hidden lg:block">
              <img 
                src="/images/external/photo-1497436072909-60f360e1d4b1.jpg"
                alt="Sustainable landscape"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* SEBI Mandate */}
      <section className="py-16 bg-navy">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-center">
            <p className="text-white/90 text-lg leading-relaxed">
              The Securities and Exchange Board of India (SEBI) mandates the <strong className="text-teal">top 1,000 listed companies</strong> by 
              market capitalization to disclose ESG performance through a rigorous <strong className="text-teal">Business Responsibility and 
              Sustainability Report (BRSR)</strong>. Non-compliance is not an option. H&G transforms this regulatory obligation into 
              a strategic competitive advantage.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Two Pillars */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            {pillars.map((pillar) => {
              const IconComponent = pillar.icon;
              return (
                <motion.div
                  key={pillar.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="service-card p-8 rounded-xl"
                >
                  <div className="w-14 h-14 bg-teal/10 rounded-xl flex items-center justify-center mb-6">
                    <IconComponent className="w-7 h-7 text-teal" />
                  </div>
                  <h3 className="font-playfair text-2xl text-navy mb-4">{pillar.title}</h3>
                  <p className="text-mid-grey">{pillar.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BRSR Coverage */}
      <section className="py-20 md:py-28 bg-light-grey">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-16">
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">Comprehensive Coverage</motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4">BRSR Coverage Areas</motion.h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {Object.entries(coverage).map(([category, items], index) => {
              const icons = [Leaf, Users, Shield];
              const titles = ['Environment', 'Social', 'Governance'];
              const IconComponent = icons[index];
              
              return (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white p-8 rounded-xl shadow-sm"
                >
                  <div className="w-12 h-12 bg-teal/10 rounded-xl flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-teal" />
                  </div>
                  <h3 className="font-semibold text-navy text-lg mb-4">{titles[index]}</h3>
                  <ul className="space-y-2">
                    {items.map((item) => (
                      <li key={item} className="flex items-center text-mid-grey text-sm">
                        <CheckCircle2 className="w-4 h-4 text-teal mr-2" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-16">
            <motion.span variants={fadeInUp} className="text-teal font-medium text-sm uppercase tracking-wider">Our Approach</motion.span>
            <motion.h2 variants={fadeInUp} className="font-playfair text-3xl md:text-4xl text-navy mt-4">4-Step Process</motion.h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
              >
                <div className="bg-light-grey p-6 rounded-xl">
                  <div className="font-bebas text-4xl text-teal mb-3">{item.step}</div>
                  <h4 className="font-semibold text-navy mb-2">{item.title}</h4>
                  <p className="text-mid-grey text-sm">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Frameworks */}
      <section className="py-16 bg-light-grey">
        <div className="max-w-7xl mx-auto px-6">
          <h3 className="text-center font-playfair text-2xl text-navy mb-8">Frameworks We Work With</h3>
          <div className="flex flex-wrap justify-center gap-4">
            {frameworks.map((framework) => (
              <span key={framework} className="px-6 py-3 bg-white rounded-full text-teal font-medium shadow-sm">
                {framework}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-teal">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-playfair text-4xl text-white mb-6">Get Your BRSR Assessment</h2>
          <p className="text-white/80 text-lg mb-8">Start your sustainability journey with a comprehensive gap analysis.</p>
          <Link to={`/${locale}/contact`} className="btn-primary inline-flex items-center">
            Schedule Assessment <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SustainabilityPage;
